import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { supplierBalanceService } from "~/services/purchasing";

import type { SupplierBalanceImportRow } from "~/services/purchasing/supplierBalanceService";

const QUERY_KEY = "supplier-balances";

/** Open AP balance summary per supplier (for the Proveedores list). */
export function useSupplierBalancesSummary() {
  return useQuery({
    queryKey: [QUERY_KEY, "summary"],
    queryFn: () => supplierBalanceService.getSummary(),
  });
}

/** A single supplier's open documents with aging (estado de cuenta). */
export function useSupplierOpenBalances(supplierId: string | undefined) {
  return useQuery({
    queryKey: [QUERY_KEY, "open", supplierId],
    queryFn: () => supplierBalanceService.getOpenBalances(supplierId!),
    enabled: !!supplierId,
  });
}

/** Per-supplier balance summary AS OF a cutoff date (KPIs al día / corte histórico). */
export function useSupplierBalancesSummaryAtCutoff(cutoff: string) {
  return useQuery({
    queryKey: [QUERY_KEY, "summary-at-cutoff", cutoff],
    queryFn: () => supplierBalanceService.getSummaryAtCutoff(cutoff),
  });
}

/** Line-level open documents at a cutoff (for the aging reports), optionally scoped per empresa. */
export function useSupplierDocumentsAtCutoff(
  cutoff: string,
  enabled: boolean,
  companyId?: string
) {
  return useQuery({
    queryKey: [QUERY_KEY, "docs-at-cutoff", cutoff, companyId ?? "all"],
    queryFn: () => supplierBalanceService.getDocumentsAtCutoff(cutoff, companyId),
    enabled,
  });
}

/** Carga de Saldos — bulk-replace opening balances for a cutoff from an uploaded file. */
export function useImportSupplierBalances() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      cutoffDate: string;
      rows: SupplierBalanceImportRow[];
      companyId: string;
    }) => supplierBalanceService.importBalances(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}
